
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Factory, Leaf, Shield, TrendingUp, Recycle, Wrench } from 'lucide-react';
import ValorisationPageLayout from '@/components/valorisation/ValorisationPageLayout';
import ValorisationContentSection from '@/components/valorisation/ValorisationContentSection';
import MetauxFerreuxSEO from '@/components/valorisation/MetauxFerreuxSEO';

const MetauxFerreux = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Recyclage et Rachat des Métaux Ferreux",
    "description": "Service professionnel de collecte, tri et recyclage des métaux ferreux. Rachat au meilleur prix avec traçabilité complète.",
    "provider": {
      "@type": "Organization",
      "name": "Groupe Fabre Environnement",
      "url": "https://www.groupefabre-environnement.fr"
    },
    "areaServed": ["Occitanie", "Auvergne-Rhône-Alpes", "Île-de-France"],
    "serviceType": "Recyclage métaux ferreux",
    "offers": {
      "@type": "Offer",
      "description": "Rachat et recyclage de tous types de métaux ferreux"
    }
  };

  const breadcrumbItems = [
    { label: "Accueil", href: "/" },
    { label: "Valorisation", href: "/valorisation" },
    { label: "Métaux ferreux", href: "/valorisation/metaux-ferreux", current: true }
  ];

  const contentBlocks = [
    {
      type: 'text' as const,
      title: "Qu'est-ce qu'un métal ferreux ?",
      content: "Les métaux ferreux sont principalement composés de fer et peuvent contenir d'autres métaux ou éléments à l'état d'alliage. Ils ont la particularité d'être magnétiques, ce qui facilite leur tri et leur séparation des autres matières. Les plus courants sont l'acier, la fonte et les alliages ferreux.",
      variant: 'highlighted' as const
    },
    {
      type: 'list' as const,
      title: "Types de métaux ferreux que nous recyclons",
      items: [
        "Ferraille : chutes d'acier, tôles, tubes, profilés",
        "Fonte : radiateurs, tuyauteries, plaques d'égout",
        "Acier inoxydable : équipements industriels, éviers",
        "Chutes de production : copeaux, tournures, débris d'usinage",
        "Aciers de construction : poutres, armatures, structures",
        "Véhicules hors d'usage : carrosseries, moteurs"
      ]
    },
    {
      type: 'process' as const,
      title: "Notre processus de recyclage",
      process: [
        {
          step: 1,
          title: "Collecte sur site",
          description: "Collecte des métaux ferreux directement chez vous ou réception dans nos centres"
        },
        {
          step: 2,
          title: "Tri et séparation",
          description: "Séparation magnétique et tri selon les différents types de métaux ferreux"
        },
        {
          step: 3,
          title: "Préparation",
          description: "Compactage et cisaillage pour optimiser le transport et la transformation"
        },
        {
          step: 4,
          title: "Acheminement",
          description: "Transport vers nos partenaires sidérurgiques pour refonte"
        },
        {
          step: 5,
          title: "Transformation",
          description: "Réintégration dans le cycle de production pour créer de nouveaux produits"
        }
      ]
    },
    {
      type: 'benefits' as const,
      title: "Les avantages du recyclage des métaux ferreux",
      benefits: [
        {
          title: "Économie d'énergie",
          description: "Jusqu'à 70% d'économie d'énergie par rapport à la production primaire",
          icon: Factory
        },
        {
          title: "Protection environnementale", 
          description: "Réduction des émissions de CO2 et préservation des ressources naturelles",
          icon: Leaf
        },
        {
          title: "Valorisation économique",
          description: "Rachat au meilleur prix avec traçabilité complète de vos déchets",
          icon: TrendingUp
        }
      ],
      variant: 'success' as const
    },
    {
      type: 'cta' as const,
      title: "Valorisez vos métaux ferreux dès aujourd'hui",
      content: "Notre équipe d'experts vous accompagne dans la valorisation de vos déchets métalliques avec des solutions personnalisées.",
      ctaText: "Demander un devis gratuit",
      ctaLink: "/contact",
      variant: 'highlighted' as const
    }
  ];

  return (
    <ValorisationPageLayout
      title="Recyclage et Rachat des Métaux Ferreux | Groupe Fabre Environnement"
      description="Valorisez vos déchets métalliques ferreux avec le Groupe Fabre Environnement. Services de collecte, rachat et recyclage pour professionnels et particuliers."
      canonical="/valorisation/metaux-ferreux"
      keywords="recyclage métaux ferreux, rachat ferraille, acier, fonte, valorisation déchets métalliques"
      structuredData={structuredData}
      heroTitle="Métaux Ferreux"
      heroSubtitle="Recyclage et valorisation professionnelle de vos déchets métalliques ferreux"
      breadcrumbItems={breadcrumbItems}
    >
      <ValorisationContentSection
        title="Recyclage professionnel des métaux ferreux"
        subtitle="Solutions complètes de collecte, tri et valorisation pour tous types de métaux ferreux"
        blocks={contentBlocks}
        className="bg-white"
      />
      
      <MetauxFerreuxSEO />
    </ValorisationPageLayout>
  );
};

export default MetauxFerreux;
